import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg, C1_FurnitureUprightContent } from "../C1_FurnitureSvg";

function HeatingSystemSymbol({ color = "currentColor" }: { color?: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M12.3 7c.8 2.2-.4 3.1-1.3 4.2-.8 1-1.2 1.8-.7 2.9.5 1 1.6 1.5 2.8 1.2 1.8-.5 2.5-2.4 1.8-4.1 1.8 1.4 2.4 3.6 1.3 5.4-1.5 2.4-5 3-7.2 1.2-2.1-1.7-2.5-4.8-.8-7 1.1-1.5 2.8-2.2 4.1-3.8Z" />
    </g>
  );
}

function HotWaterTankSymbol({ color = "currentColor" }: { color?: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="6" ry="3" />
      <path d="M6 5v14c0 1.7 2.7 3 6 3s6-1.3 6-3V5" />
      <path d="M9 13c0-2 3-2.4 3-4.5 1.8 1.6 3 3 3 4.8a3 3 0 0 1-6-.3Z" />
    </g>
  );
}

function EquipmentRenderer({
  width,
  height,
  rotation = 0,
  PX,
  strokeWidth = 2,
  strokeColor = "#000000",
  fillColor = "#ffffff",
  ownerRotation = 0,
  ownerReflect = false,
  kind,
}: C1_FurnitureRendererProps & { kind: "heating" | "tank" }) {
  const iconSize = Math.min(width, height) * 0.55;
  return (
    <C1_FurnitureSvg
      PX={PX}
      width={width}
      height={height}
      rotation={rotation}
      viewBoxX={-width / 2}
      viewBoxY={-height / 2}
      viewBoxWidth={width}
      viewBoxHeight={height}
      originXPercentage={50}
      originYPercentage={kind === "heating" ? 0 : 50}
    >
      {kind === "tank" ? (
        <ellipse cx={0} cy={0} rx={width / 2 - 2} ry={height / 2 - 2} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth * 2} />
      ) : (
        <rect x={-width / 2 + 2} y={-height / 2 + 2} width={width - 4} height={height - 4} rx={4} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth * 2} />
      )}
      <C1_FurnitureUprightContent
        anchorX={0}
        anchorY={0}
        ownerRotation={Number(ownerRotation)}
        ownerReflect={Boolean(ownerReflect)}
      >
        <svg x={-iconSize / 2} y={-iconSize / 2} width={iconSize} height={iconSize} viewBox="0 0 24 24" aria-hidden="true">
          {kind === "heating" ? (
            <path
              d="M12.3 7c.8 2.2-.4 3.1-1.3 4.2-.8 1-1.2 1.8-.7 2.9.5 1 1.6 1.5 2.8 1.2 1.8-.5 2.5-2.4 1.8-4.1 1.8 1.4 2.4 3.6 1.3 5.4-1.5 2.4-5 3-7.2 1.2-2.1-1.7-2.5-4.8-.8-7 1.1-1.5 2.8-2.2 4.1-3.8Z"
              fill="none"
              stroke={strokeColor}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : <HotWaterTankSymbol color={strokeColor} />}
        </svg>
      </C1_FurnitureUprightContent>
    </C1_FurnitureSvg>
  );
}

export const HeatingSystem: C1_FurnitureDefinition = {
  id: "heatingSystem",
  hybridEquipment: true,
  icon: <svg viewBox="0 0 24 24" aria-hidden="true"><HeatingSystemSymbol /></svg>,
  description: { de: "Heizung / Wärmepumpe", en: "Heating / heat pump" },
  category: RoomType.HeatingRoom,
  defaultWidth: 0.6,
  defaultHeight: 0.4,
  originXPercentage: 50,
  originYPercentage: 0,
  wallDockable: true,
  grabsW: true,
  grabsH: true,
  autoRotate: true,
  autoReflect: true,
  properties: [],
  Renderer: (props) => <EquipmentRenderer {...props} kind="heating" />,
};

export const HotWaterTank: C1_FurnitureDefinition = {
  id: "hotWaterTank",
  icon: <svg viewBox="0 0 24 24" aria-hidden="true"><HotWaterTankSymbol /></svg>,
  description: { de: "Warmwassertank", en: "Hot-water tank" },
  category: RoomType.HeatingRoom,
  defaultWidth: 0.8,
  defaultHeight: 0.8,
  originXPercentage: 50,
  originYPercentage: 50,
  grabsW: true,
  grabsH: true,
  properties: [],
  Renderer: (props) => <EquipmentRenderer {...props} kind="tank" />,
};
